import { cn } from '@/lib/cn';
import { isImageType } from '@/utils/fileHelpers';
import {
  responseBodyTextClass,
  responseQuestionWidthClass,
  useProseDensity,
} from '@heidi/answer-kit';
import { FileCard } from '@heidi/design-system/file-card';
import { TypographyH3, TypographyP } from '@heidi/design-system/typography';
import { memo } from 'react';

// ===== USER MESSAGE COMPONENT =====

/** Maximum character length for displaying user queries in branded H3 style.
 * Queries longer than this will use the chat bubble style instead. */
const USER_QUERY_BRANDED_STYLE_MAX_LENGTH = 100;

interface UserMessageProps {
  message: string;
  files?: Array<{
    name: string;
    type: string;
    file_key?: string;
    url?: string; // Presigned URL for displaying images/files
  }>;
  className?: string;
  /** Unique identifier for scroll navigation */
  messageId?: string;
  /** Whether this is the first user message in the conversation */
  isFirstUserMessage?: boolean;
  /** Whether the chat is displayed in full-screen mode (vs sidebar) */
  isFullScreenEvidence?: boolean;
}

// User query styled conditionally: first short query in full-screen uses branded H3 style,
// all others use chat bubble (EVI-265)
// OPTIMIZATION: Wrapped in memo to prevent re-renders when parent re-renders
// but props haven't changed (e.g., during streaming state updates)
export const UserMessage = memo(function UserMessage({
  message,
  files,
  className,
  messageId,
  isFirstUserMessage = false,
  isFullScreenEvidence = false,
}: UserMessageProps): JSX.Element {
  // `isFullScreenEvidence` is the fallback for a bubble rendered outside a
  // density-publishing host; `Chat` publishes one from the same signal.
  const bubbleDensity = useProseDensity(isFullScreenEvidence ? 'comfortable' : 'compact');
  const hasFiles = files && files.length > 0;
  // Only use branded style when ALL conditions are met:
  // 1. Is the first user message
  // 2. Is in full-screen evidence mode (not sidebar)
  // 3. Has no file attachments
  // 4. Message is short enough
  const useBrandedStyle =
    isFirstUserMessage &&
    isFullScreenEvidence &&
    !hasFiles &&
    message.length <= USER_QUERY_BRANDED_STYLE_MAX_LENGTH;

  // Short queries without files: use branded Exposure H3 style
  if (useBrandedStyle) {
    return (
      <div
        className={cn('flex w-full min-w-0 scroll-mt-2.5', className)}
        data-message-id={messageId}
        data-user-message="true"
      >
        {/*
          - text-text-secondary is intentional: H3 is WCAG large text (24px),
            3:1 threshold.
          - font-light overrides TypographyH3's default font-medium (500) to
            Exposure Light (300) per the branded-title display treatment.
            Exposure is already inherited from the theme-level rule on h1-h3.
          - No vertical margin of its own, top or bottom. Both gaps around a
            question belong to the thread, not to this shape: `mb-6` here is
            why a short question sat 40px above its answer and a long one
            16px, and `mt-2.5` was an extra 10px on top of whatever the column
            had already spent. `data-user-message` is the hook the turn
            container's question-to-answer rule selects on, so every question
            shape in both chat families takes the same rung — see
            `response-thread`. `scroll-mt-*` stays: it is snap offset, not
            spacing.
        */}
        <TypographyH3
          className="break-words font-light text-text-secondary"
          data-chat-branded-title="true"
        >
          {message}
        </TypographyH3>
      </div>
    );
  }

  // Long queries or queries with files: use chat bubble style.
  // The question sits directly above the answer, so its size is the answer's —
  // read from the same density the prose uses rather than branched here. This
  // previously read `isFullScreenEvidence ? 'text-base' : 'sm:text-sm'`, which
  // had no base size at all: below the `sm` VIEWPORT breakpoint it fell through
  // to the component default, and above it the bubble sat at 14px next to a
  // 16px answer.
  return (
    <div
      className={cn('flex w-full scroll-mt-2.5 items-start justify-end', className)}
      data-message-id={messageId}
      data-user-message="true"
    >
      <div
        className={cn(
          'box-border flex flex-col items-start justify-end gap-1 rounded-lg bg-background-secondary px-3 py-1.5',
          responseQuestionWidthClass(bubbleDensity)
        )}
      >
        {hasFiles && (
          <div className="mb-1 flex flex-wrap gap-1.5">
            {files.map((file, index) =>
              file.url && isImageType(file.type) ? (
                <div
                  key={file.file_key || `${file.name}-${index}`}
                  className="size-14 overflow-hidden rounded-lg border border-border bg-surface"
                  data-file-key={file.file_key}
                >
                  <img
                    src={file.url}
                    alt={file.name}
                    className="size-full object-cover"
                  />
                </div>
              ) : (
                <FileCard
                  key={file.file_key || `${file.name}-${index}`}
                  fileName={file.name}
                  fileType={file.name.split('.').pop()?.toUpperCase()}
                />
              )
            )}
          </div>
        )}
        <TypographyP
          className={cn(
            // `[overflow-wrap:anywhere]` (vs Tailwind's `break-words`) lets the
            // browser break long unbreakable strings — e.g. delivery tokens
            // inside the patient-summary preview URL — when computing
            // min-content, so they wrap inside the bubble instead of
            // overflowing the `max-w-*` cap.
            'w-full whitespace-pre-wrap break-words text-text-primary [overflow-wrap:anywhere]',
            responseBodyTextClass(bubbleDensity)
          )}
        >
          {message}
        </TypographyP>
      </div>
    </div>
  );
});
